import { useCallback, useEffect, useState } from 'react';
import { Button } from 'primereact/button';
import { Column } from 'primereact/column';
import { DataTable, DataTablePageEvent } from 'primereact/datatable';
import { Dialog } from 'primereact/dialog';
import { formatDateTime } from 'medere-ui-framework';
import { StockMovementDTO } from '../../dto/SectorRepositoryDTO';
import { getRepositoryItemMovements } from '../../services/SectorRepositoryController';
import { parseApiError } from '../../services/apiError';
import useNotify from '../../hooks/useNotify';

const PAGE_SIZE = 20;

const quantityFormat = new Intl.NumberFormat('es-AR', { maximumFractionDigits: 2 });

const orEmpty = (value: string | null | undefined) => (value == null || value.trim() === '' ? '—' : value);

interface Props {
    item: { repositoryDetail: number; description: string } | null;
    sectorName: string;
    onHide: () => void;
}

export default function StockMovementsDialog({ item, sectorName, onHide }: Props) {
    const notify = useNotify();
    const [movements, setMovements] = useState<StockMovementDTO[]>([]);
    const [totalRecords, setTotalRecords] = useState(0);
    const [first, setFirst] = useState(0);
    const [loading, setLoading] = useState(false);

    const load = useCallback(async (repositoryDetail: number, offSet: number) => {
        setLoading(true);
        try {
            const page = await getRepositoryItemMovements(repositoryDetail, offSet, PAGE_SIZE);
            setMovements(page.items ?? []);
            setTotalRecords(page.totalRecords ?? 0);
        } catch (error: unknown) {
            const apiError = parseApiError(error);
            console.error(apiError.errorCode, apiError.path);
            notify.error(apiError.status >= 400 && apiError.status < 500 ? apiError.message : 'No se pudo cargar el historial. Intentá de nuevo.');
        } finally {
            setLoading(false);
        }
    }, [notify]);

    useEffect(() => {
        if (!item) return;
        setFirst(0);
        load(item.repositoryDetail, 0);
    }, [item, load]);

    function handlePage(event: DataTablePageEvent) {
        if (!item) return;
        setFirst(event.first);
        load(item.repositoryDetail, event.first);
    }

    return (
        <Dialog
            header={`Historial de movimientos · ${item?.description ?? ''}`}
            visible={item != null}
            onHide={onHide}
            footer={<Button type="button" className="buttonMain" label="Cerrar" onClick={onHide} />}
            breakpoints={{ '640px': '100vw' }}
            className="nv-scope nv-dialog stock-sectors-view"
            modal
        >
            <div className="nv-dialog-body">
                <p className="nv-note">
                    <i className="pi pi-info-circle" aria-hidden="true" />
                    {sectorName}
                </p>
                <DataTable
                    value={movements}
                    dataKey="stockmovement"
                    lazy
                    paginator
                    rows={PAGE_SIZE}
                    first={first}
                    totalRecords={totalRecords}
                    onPage={handlePage}
                    loading={loading}
                    scrollable
                    scrollHeight="420px"
                    emptyMessage="Todavía no hay movimientos de este artículo."
                    className="nv-list-table"
                >
                    <Column header="Fecha y hora" className="nv-col-date" body={(m: StockMovementDTO) => formatDateTime(m.stockdocument_date)} />
                    <Column
                        header="Movimiento"
                        className="nv-col-text"
                        body={(m: StockMovementDTO) => (
                            <span className="nv-tip" data-pr-tooltip={`Documento N.° ${String(m.stockdocument).padStart(8, '0')}`}>
                                {m.stockdocumenttype_desc}
                            </span>
                        )}
                    />
                    <Column header="Usuario" className="nv-col-text" body={(m: StockMovementDTO) => orEmpty(m.stockdocument_userinfo)} />
                    <Column
                        header="Información adicional"
                        className="nv-col-text"
                        body={(m: StockMovementDTO) => {
                            const info = orEmpty(m.stockdocument_additionalinformation);
                            return <span className="nv-truncate nv-tip" data-pr-tooltip={info === '—' ? undefined : info}>{info}</span>;
                        }}
                    />
                    <Column header="Cantidad" align="right" alignHeader="right" className="nv-col-qty" body={(m: StockMovementDTO) => quantityFormat.format(m.quantity)} />
                    <Column header="Stock" align="right" alignHeader="right" className="nv-col-qty" body={(m: StockMovementDTO) => quantityFormat.format(m.balance)} />
                </DataTable>
            </div>
        </Dialog>
    );
}
